import React, { useRef, useState, useEffect, useCallback } from 'react';
import { useFlight } from '../contexts/FlightContext';
import { STATIONS, Station, TimestampEntry, UserRole } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { database } from '../services/firebase';
import { ref, onValue } from 'firebase/database';
import NewsInputField from '../components/NewsInputField';

const useDebouncedCallback = (callback: (...args: any[]) => void, delay: number) => {
    const timeoutRef = useRef<number>();
    
    useEffect(() => {
        return () => {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);
        }
    }, []);

    return useCallback((...args: any[]) => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
        timeoutRef.current = window.setTimeout(() => {
            callback(...args);
        }, delay);
    }, [callback, delay]);
};

const DebouncedInputField: React.FC<{
    id: string;
    label: string;
    value: string;
    onSave: (val: string) => void;
    type?: string;
    placeholder?: string;
    className?: string;
}> = React.memo(({ id, label, value, onSave, type = 'text', placeholder, className }) => {
    const [localValue, setLocalValue] = useState(value);
    const debouncedSave = useDebouncedCallback(onSave, 400);

    useEffect(() => {
        if (value !== localValue) {
            setLocalValue(value);
        }
    }, [value]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setLocalValue(e.target.value);
        debouncedSave(e.target.value);
    };

    return (
        <div>
            <label htmlFor={id} className="block text-center text-sm font-medium text-amber-400 mb-1">{label}</label>
            <input
                id={id}
                type={type}
                value={localValue}
                onChange={handleChange}
                className={`w-full text-center ${className}`}
                placeholder={placeholder}
            />
        </div>
    );
});

const DebouncedTextareaField: React.FC<{
    id: string;
    label: string;
    value: string;
    onSave: (val: string) => void;
    rows?: number;
    placeholder?: string;
}> = React.memo(({ id, label, value, onSave, rows, placeholder }) => {
    const [localValue, setLocalValue] = useState(value);
    const debouncedSave = useDebouncedCallback(onSave, 400);

    useEffect(() => {
        if (value !== localValue) {
            setLocalValue(value);
        }
    }, [value]);

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setLocalValue(e.target.value);
        debouncedSave(e.target.value);
    };

    return (
        <div>
            <label htmlFor={id} className="block text-sm font-medium text-slate-400 mb-1">{label}</label>
            <textarea
                id={id}
                rows={rows}
                value={localValue}
                onChange={handleChange}
                className="w-full"
                placeholder={placeholder}
            />
        </div>
    );
});

const CrewMovementScreen: React.FC = () => {
  const { selectedFlightData: flightData, updateCrewMovement, updateFlightData, updateCounterDistributionField, addCapturedDoc, showNotification, updateAtsrField, finalizeAcceptance, triggerSystemClear, triggerGateClear } = useFlight();
  const { user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isCapturing, setIsCapturing] = useState(false);
  
  if (!flightData) return null;

  const [flightNumbers, setFlightNumbers] = useState<string[]>([]);
  
  const etd = flightData.counterDistribution.etd || '';
  const etdDate = etd.split('T')[0] || '';
  
  const getLatestTimestamp = (station: Station, type: 'crew' | 'pilots'): TimestampEntry | null => {
      const entries = flightData.crewMovement[station]?.[type];
      return entries && entries.length > 0 ? entries[entries.length - 1] : null;
  }

  useEffect(() => {
    const flightNumbersRef = ref(database, 'config/flightNumbers');
    const unsubscribe = onValue(flightNumbersRef, (snapshot) => {
        setFlightNumbers(snapshot.val() || []);
    });

    return () => unsubscribe();
  }, []);

  const handleDateChange = (date: string) => {
    if (date) {
      updateCounterDistributionField('root', 'etd', `${date}T00:00:00`);
    } else {
      updateCounterDistributionField('root', 'etd', '');
    }
  };

  const handleTimestamp = (station: Station, type: 'crew' | 'pilots') => {
    updateCrewMovement(station, type);
    showNotification(`${type === 'crew' ? 'Crew' : 'Pilot'} time for ${station} recorded.`);
  };
  
  const handleFinalizeClick = () => {
    if (window.confirm("PLEASE FINALISE ACCEPTANCE. This action will be recorded and cannot be undone.")) {
        finalizeAcceptance();
    }
  };

  const handleSystemClearClick = () => {
    if (window.confirm("Confirm SYSTEM CLEAR. This finalizes passenger and flight data in the system.")) {
        triggerSystemClear();
        showNotification('System Clear recorded.');
    }
  };

  const handleGateClearClick = () => {
      if (window.confirm("Confirm GATE CLEAR. This signifies all gate activities are complete.")) {
          triggerGateClear();
          showNotification('Gate Clear recorded.');
      }
  };

  const handleCheckboxChange = (key: 'immigrationApproved' | 'crewAndPilotsArrivedTogether', checked: boolean) => {
    updateFlightData(key, checked);
    if (key === 'immigrationApproved') {
        showNotification('Immigration status updated.');
    } else {
        showNotification('Paired arrival mode updated.');
    }
  };

  const handleCaptureClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      setIsCapturing(true);
      const reader = new FileReader();
      reader.onloadend = () => {
        const newDoc = {
          url: reader.result as string,
          capturedAt: new Date().toISOString(),
          by: user?.username || 'Unknown',
        };
        addCapturedDoc(newDoc);
        setIsCapturing(false);
        showNotification('Document captured successfully!');
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <div className="space-y-6">
      <div className="bg-slate-900/60 backdrop-blur-md border border-slate-700 rounded-xl shadow-lg p-4">
        <h2 className="text-lg font-bold text-blue-400 border-b border-slate-700 pb-2 mb-4">Flight Setup</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label htmlFor="flightNoSelect" className="block text-sm font-medium text-slate-400 mb-1">Flight Number</label>
            <select
              id="flightNoSelect"
              value={flightData.counterDistribution.flightNo}
              onChange={(e) => updateCounterDistributionField('root', 'flightNo', e.target.value)}
              className="w-full"
            >
              <option value="" disabled>Select a flight</option>
              {flightNumbers.map(fn => <option key={fn} value={fn}>{fn}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="flightDate" className="block text-sm font-medium text-slate-400 mb-1">Flight Date</label>
            <input
              type="date"
              id="flightDate"
              value={etdDate}
              onChange={(e) => handleDateChange(e.target.value)}
              className="w-full"
            />
          </div>
        </div>
        <p className="text-slate-400 text-sm mt-4 text-center">Manage flight details and track crew movements below. All fields are always editable.</p>
      </div>
      
      <div className="bg-slate-900/60 backdrop-blur-md border border-slate-700 rounded-xl shadow-lg p-4 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center flex-wrap gap-x-4 gap-y-2">
          <div className="flex items-center">
            <input
              id="crewAndPilotsArrivedTogether"
              type="checkbox"
              checked={!!flightData.crewAndPilotsArrivedTogether}
              onChange={(e) => handleCheckboxChange('crewAndPilotsArrivedTogether', e.target.checked)}
              className="w-4 h-4 text-blue-600 bg-slate-700 border-slate-600 rounded focus:ring-blue-500"
            />
            <label htmlFor="crewAndPilotsArrivedTogether" className="ml-2 text-sm font-medium text-slate-300">Crew &amp; Pilots Arrived Together</label>
          </div>
          <div className="flex items-center gap-2">
              <div className="flex items-center">
              <input
                  id="immigrationApproved"
                  type="checkbox"
                  checked={!!flightData.immigrationApproved}
                  onChange={(e) => handleCheckboxChange('immigrationApproved', e.target.checked)}
                  className="w-4 h-4 text-blue-600 bg-slate-700 border-slate-600 rounded focus:ring-blue-500"
              />
              <label htmlFor="immigrationApproved" className="ml-2 text-sm font-medium text-slate-300">Immigration Approved</label>
              </div>
              {flightData.immigrationApproved && (
                  <input
                      type="text"
                      placeholder="Add a comment..."
                      value={flightData.immigrationComment || ''}
                      onChange={(e) => updateFlightData('immigrationComment', e.target.value)}
                      className="py-1 px-2 text-sm"
                  />
              )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input
            type="file"
            accept="image/*"
            capture="environment"
            ref={fileInputRef}
            onChange={handleFileChange}
            className="hidden"
          />
          <button
            onClick={handleCaptureClick}
            disabled={isCapturing}
            className="flex items-center bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700 text-white font-bold py-2 px-4 rounded-lg transition-all duration-200 transform hover:scale-105 disabled:bg-slate-500"
          >
            {isCapturing ? 'Capturing...' : 'Capture Document'}
          </button>
          {user && [UserRole.ADMIN, UserRole.SUPERVISOR, UserRole.AGENT].includes(user.role) && (
            <>
               <button
                  onClick={handleSystemClearClick}
                  disabled={!!flightData.systemClear}
                  className="flex items-center bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white font-bold py-2 px-4 rounded-lg transition-all duration-200 transform hover:scale-105 disabled:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Finalize system data (passenger manifest, etc.)"
                >
                  System Clear
              </button>
              <button
                  onClick={handleGateClearClick}
                  disabled={!!flightData.gateClear}
                  className="flex items-center bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 text-white font-bold py-2 px-4 rounded-lg transition-all duration-200 transform hover:scale-105 disabled:bg-slate-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Confirm all gate activities are complete"
                >
                  Gate Clear
              </button>
              {!flightData.acceptanceFinalized && (
                <button
                  onClick={handleFinalizeClick}
                  className="flex items-center bg-gradient-to-r from-amber-600 to-red-700 hover:from-amber-700 hover:to-red-800 text-white font-bold py-2 px-4 rounded-lg transition-all duration-200 transform hover:scale-105"
                  title="Finalize acceptance even if doors are not closed. This is for exceptional cases."
                >
                  Force Finalize
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {user && [UserRole.ADMIN, UserRole.SUPERVISOR, UserRole.AGENT].includes(user.role) && (
        <NewsInputField />
      )}

      <div className="flex flex-wrap -m-2">
        {STATIONS.map((station, index) => {
            const crewTimestamp = getLatestTimestamp(station, 'crew');
            const pilotTimestamp = getLatestTimestamp(station, 'pilots');
            const showPilotButton = STATIONS.indexOf(station) <= STATIONS.indexOf('At Aircraft');

            return (
                <div key={station} data-tour-id={index === 0 ? 'crew-movement-station' : undefined} className="w-full md:w-1/3 lg:w-1/4 p-2">
                    <div className="bg-slate-900/60 backdrop-blur-md border border-slate-700 rounded-xl shadow-lg flex flex-col p-3 transition-all duration-300 hover:border-blue-500/50 hover:shadow-blue-500/10 h-full">
                        <h3 className="font-semibold text-center text-slate-100 mb-2 text-sm h-10 flex items-center justify-center">{station}</h3>
                        <div className="flex-grow flex items-start justify-center space-x-2">
                            <div className="flex-1 flex flex-col items-center space-y-2">
                                <button 
                                    onClick={() => handleTimestamp(station, 'crew')}
                                    className={`w-20 h-12 flex items-center justify-center rounded-lg transition-all duration-200 ${crewTimestamp ? 'bg-blue-600 text-white shadow-[0_0_15px_rgba(59,130,246,0.6)]' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'}`}
                                    aria-label={`Record crew time for ${station}`}
                                >
                                    Crew
                                </button>
                                <div className="h-4 text-xs font-mono text-slate-400">
                                    {crewTimestamp ? new Date(crewTimestamp.time).toLocaleTimeString('en-GB', {hour: '2-digit', minute:'2-digit'}) : '--:--'}
                                </div>
                            </div>
                            
                            {showPilotButton && (
                                <>
                                    <div className="h-16 w-px bg-slate-700"></div>
                                    <div className="flex-1 flex flex-col items-center space-y-2">
                                        <button 
                                            onClick={() => handleTimestamp(station, 'pilots')}
                                            className={`w-20 h-12 flex items-center justify-center rounded-lg transition-all duration-200 ${pilotTimestamp ? 'bg-amber-500 text-white shadow-[0_0_15px_rgba(245,158,11,0.6)]' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'}`}
                                            aria-label={`Record pilot time for ${station}`}
                                        >
                                            Pilots
                                        </button>
                                          <div className="h-4 text-xs font-mono text-slate-400">
                                            {pilotTimestamp ? new Date(pilotTimestamp.time).toLocaleTimeString('en-GB', {hour: '2-digit', minute:'2-digit'}) : '--:--'}
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                        {station === '1st X-ray' && (
                            <div className="mt-2 pt-2 border-t border-slate-600">
                                <DebouncedInputField
                                    id="finalFuel"
                                    label="Final Fuel"
                                    value={flightData.counterDistribution.finalFigures['Final Fuel'] || ''}
                                    onSave={(val) => updateCounterDistributionField('finalFigures', 'Final Fuel', val)}
                                    placeholder="Enter value"
                                />
                            </div>
                        )}
                         {station === 'Doors Closed' && (
                            <div className="mt-2 pt-2 border-t border-slate-700 w-full">
                                {crewTimestamp && !flightData.acceptanceFinalized ? (
                                    <button
                                        onClick={handleFinalizeClick}
                                        className="w-full bg-amber-600 hover:bg-amber-700 text-white font-bold py-2 px-2 rounded-lg text-sm animate-pulse"
                                    >
                                        FINALIZE ACCEPTANCE
                                    </button>
                                ) : flightData.acceptanceFinalized ? (
                                    <div className="text-center bg-green-500/20 border border-green-500 text-green-300 text-xs font-bold py-1 px-2 rounded-md">
                                        Finalized: {new Date(flightData.acceptanceFinalized.time).toLocaleTimeString('en-GB', {hour: '2-digit', minute:'2-digit'})}
                                        <br/>
                                        by {flightData.acceptanceFinalized.by}
                                    </div>
                                ) : null}
                            </div>
                         )}
                    </div>
                </div>
            )
        })}
      </div>
      
       <div className="bg-slate-900/60 backdrop-blur-md border border-slate-700 rounded-xl shadow-lg p-4">
        <div className="flex items-center gap-4 border-b border-slate-700 pb-2 mb-4">
            <h2 className="text-lg font-bold text-blue-400">ATSR No</h2>
            <DebouncedInputField
                id="atsrText"
                label=""
                value={flightData.atsrData?.atsrText || ''}
                onSave={(val) => updateAtsrField('atsrText', val)}
                className="py-1 px-2 text-sm flex-grow"
                placeholder="Enter ATSR identifier..."
            />
        </div>
        <p className="text-slate-400 text-sm mb-4 -mt-2">Fill in the Air Transport Service Request details below. All fields are optional.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <DebouncedInputField
            id="excessBaggage"
            label="1st Excess Baggage"
            type="number"
            value={flightData.atsrData?.excessBaggage || ''}
            onSave={(val) => updateAtsrField('excessBaggage', val)}
            placeholder="Number of bags"
          />
          <DebouncedInputField
            id="heavyBags"
            label="Heavy Bag"
            type="number"
            value={flightData.atsrData?.heavyBags || ''}
            onSave={(val) => updateAtsrField('heavyBags', val)}
            placeholder="Number of bags"
          />
          <DebouncedInputField
            id="aup"
            label="AUP"
            type="number"
            value={flightData.atsrData?.aup || ''}
            onSave={(val) => updateAtsrField('aup', val)}
            placeholder="Value"
          />
        </div>
        <div className="mt-4">
            <DebouncedTextareaField
              id="atsrNotes"
              label="Notes"
              rows={3}
              value={flightData.atsrData?.notes || ''}
              onSave={(val) => updateAtsrField('notes', val)}
              placeholder="Add any other notes..."
            />
        </div>
      </div>
    </div>
  );
};

export default CrewMovementScreen;